import Image from "next/image";
import Icon from "../atoms/Icon";
import PrivacyPolicyLink from "../molecules/PrivacyPolicyLink";

interface AuthLayoutProps {
  /** Form content rendered in the left panel. */
  children: React.ReactNode;
}

/**
 * @component AuthLayout
 * @description Two-column layout template for authentication screens.
 * Left panel contains the GFS logo, form content, and a privacy footer.
 * Right panel displays a full-height hero image (hidden on mobile).
 * @param children - Form content rendered in the center of the left panel.
 * @returns The full-screen auth layout with left form panel and right hero image.
 */
// TODO: Mobile layout pending — waiting on mobile designs before implementing responsive version
export default function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex bg-gfs-off-white">
      {/* Left panel */}
      <main className="w-full md:w-[50%] flex flex-col justify-between">
        <div className="w-full flex justify-center pt-4 pb-4 md:pt-12 md:pb-12 lg:pt-16 border-b border-border-subtle">
          <Icon
            name="welcome"
            className="h-12 w-12 md:h-14 md:w-14 filter brightness-30 grayscale-100"
            alt="Grounds for Sculpture logo"
            decorative={false}
          />
          <hr className="hidden" />
        </div>
        {/* Right panel — hero image (desktop only) */}
        <div className="w-full md:hidden h-[210px] overflow-hidden relative">
          <Image
            src="/gfs_auth_image.png"
            alt="Grounds for Sculpture park"
            fill
            className="object-cover"
            priority
          />
        </div>

        <div className="max-w-md w-full mx-auto pt-8 md:pt-16 lg:pt-20 px-6 md:px-12 lg:px-16 pb-6 md:max-w-none">
          {children}
        </div>

        <footer className="text-center mt-auto px-6 md:px-12 lg:px-16 py-6 flex justify-center">
          <PrivacyPolicyLink />
        </footer>
      </main>

      {/* Right panel — hero image (desktop only) */}
      <div className="hidden md:block md:w-[50%] relative">
        <Image
          src="/gfs_auth_image.png"
          alt="Grounds for Sculpture park"
          fill
          className="object-cover"
          priority
        />
      </div>
    </div>
  );
}
